import type {
  WidgetConfig,
  BusinessPurposeWeekData,
  UtilizationDataPoint,
  HeatmapCell,
  BusinessPurposeConfig,
  RoomDetail,
  RoomTypeConfig,
  ScheduleEvent,
  BusinessGroupConsumption,
  SurveySiteCSAT,
  SurveyTrendPoint,
  SpaceTypeSatisfaction,
  PurposeSatisfaction,
} from '@/types/reports';
import { BusinessPurposeChart } from '../BusinessPurposeChart';
import { UtilizationTrendChart } from '../UtilizationTrendChart';
import { UtilizationHeatmap } from '../UtilizationHeatmap';
import { RoomUtilizationRanking } from '../RoomUtilizationRanking';
import { RoomTypeBarChart } from '../RoomTypeBarChart';
import { RoomDetailTable } from '../RoomDetailTable';
import { RunOfShowTimeline } from '../RunOfShowTimeline';
import { GroupPurposeChart } from '../GroupPurposeChart';
import { GroupSpaceTypeChart } from '../GroupSpaceTypeChart';
import { GroupConsumptionTable } from '../GroupConsumptionTable';
import { CSATSiteCards } from '../CSATSiteCards';
import { CSATTrendChart } from '../CSATTrendChart';
import { SpaceTypeSatisfactionChart } from '../SpaceTypeSatisfactionChart';
import { CSATByPurposeChart } from '../CSATByPurposeChart';
import { CSATBySpaceTypeChart } from '../CSATBySpaceTypeChart';

interface DashboardWidgetRendererProps {
  widget: WidgetConfig;
  data: unknown;
  purposes?: BusinessPurposeConfig[];
  roomTypes?: RoomTypeConfig[];
  scheduleEvents?: ScheduleEvent[];
  largeEventThreshold?: number;
}

export const DashboardWidgetRenderer = ({
  widget,
  data,
  purposes,
  roomTypes,
  scheduleEvents,
  largeEventThreshold,
}: DashboardWidgetRendererProps) => {
  switch (widget.type) {
    case 'stacked-bar':
      return (
        <BusinessPurposeChart data={data as BusinessPurposeWeekData[]} purposes={purposes ?? []} />
      );
    case 'line':
      return <UtilizationTrendChart data={data as UtilizationDataPoint[]} />;
    case 'heatmap':
      return <UtilizationHeatmap data={data as HeatmapCell[]} />;
    case 'horizontal-bar':
      if (widget.variant === 'ranking')
        return <RoomUtilizationRanking data={data as RoomDetail[]} />;
      if (widget.variant === 'by-type-pct')
        return (
          <RoomTypeBarChart
            data={data as RoomDetail[]}
            roomTypes={roomTypes ?? []}
            mode="utilization"
            titleKey={widget.titleKey}
          />
        );
      if (widget.variant === 'by-type-count')
        return (
          <RoomTypeBarChart
            data={data as RoomDetail[]}
            roomTypes={roomTypes ?? []}
            mode="bookings"
            titleKey={widget.titleKey}
          />
        );
      return null;
    case 'data-table':
      return <RoomDetailTable data={data as RoomDetail[]} roomTypes={roomTypes ?? []} />;
    case 'schedule-timeline':
      return (
        <RunOfShowTimeline
          events={scheduleEvents ?? (data as ScheduleEvent[]) ?? []}
          roomTypes={roomTypes ?? []}
          largeEventThreshold={largeEventThreshold ?? 40}
        />
      );
    case 'group-purpose-bar':
      return (
        <GroupPurposeChart data={data as BusinessGroupConsumption[]} title={widget.titleKey} />
      );
    case 'group-space-bar':
      return (
        <GroupSpaceTypeChart data={data as BusinessGroupConsumption[]} title={widget.titleKey} />
      );
    case 'group-consumption-table':
      return (
        <GroupConsumptionTable data={data as BusinessGroupConsumption[]} title={widget.titleKey} />
      );
    case 'survey-site-grid':
      return <CSATSiteCards data={data as SurveySiteCSAT[]} titleKey={widget.titleKey} />;
    case 'survey-trend-line':
      return <CSATTrendChart data={data as SurveyTrendPoint[]} titleKey={widget.titleKey} />;
    case 'survey-polar':
      return (
        <SpaceTypeSatisfactionChart
          data={data as SpaceTypeSatisfaction[]}
          titleKey={widget.titleKey}
        />
      );
    case 'survey-purpose-bar':
      return <CSATByPurposeChart data={data as PurposeSatisfaction[]} titleKey={widget.titleKey} />;
    case 'survey-space-bar':
      return (
        <CSATBySpaceTypeChart data={data as SpaceTypeSatisfaction[]} titleKey={widget.titleKey} />
      );
    default:
      return null;
  }
};

DashboardWidgetRenderer.displayName = 'DashboardWidgetRenderer';
